/**
 * Matriz Módulo × Canal para a tela de preferências de notificação.
 *
 * Linhas = módulos do sistema (ícones do popover legado para identidade visual).
 * Colunas = canais cadastrados em `notification_channels`.
 * Cada célula é um Switch que dispara um upsert via RPC `set_user_channel_preference`.
 *
 * Canais não implementados (ver IMPLEMENTED_CHANNELS no hook) recebem badge
 * "Em breve" e ficam `disabled` até a fase de email/SMS/WhatsApp.
 *
 * @version 1.3.0 - 2026-09-07 — Módulo 'settings' exibido como "Exportações"
 * @version 1.2.0 - 2026-06-05 — Coluna fixa "Módulo" agora 100% opaca (bg-card + shadow divisor), eliminando sobreposição ao rolar horizontalmente
 * @version 1.1.0 - 2026-06-05 — Substituído Switch por IconToggle (padrão Departamentos)
 * @version 1.0.0 - 2026-06-05 — F3 concluída
 */
import {
  MessageSquare, AtSign, Calendar, FileText, DollarSign,
  CheckSquare, ListChecks, Briefcase, Bell, FileDown, Volume2, Globe, Smartphone, Mail, MessageCircle,
  type LucideIcon,
} from 'lucide-react';
import { IconToggle } from '@/components/ui/icon-toggle';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useNotificationDelivery } from '@/hooks/useNotificationDelivery';
import { showToast } from '@/lib/toast';
import type {
  NotificationChannel,
  ChannelPreference,
} from '@/hooks/useNotificationSettings';


/** Ícone + rótulo amigável por módulo (paridade com o popover legado). */
const MODULE_META: Record<string, { label: string; icon: LucideIcon }> = {
  chat:       { label: 'Conversas',    icon: MessageSquare },
  activities: { label: 'Atividades',   icon: ListChecks },
  scheduling: { label: 'Agendamentos', icon: Calendar },
  contracts:  { label: 'Contratos',    icon: FileText },
  financial:  { label: 'Financeiro',   icon: DollarSign },
  tasks:      { label: 'Tarefas',      icon: CheckSquare },
  crm:        { label: 'CRM',          icon: Briefcase },
  // Avisos de exportação de dados (módulo técnico 'settings')
  settings:   { label: 'Exportações',  icon: FileDown },
};

/** Ícone por canal (apenas decorativo). */
const CHANNEL_ICON: Record<string, LucideIcon> = {
  in_app:   Bell,
  sound:    Volume2,
  browser:  Globe,
  push_fcm: Smartphone,
  email:    Mail,
  sms:      MessageCircle,
  whatsapp: MessageCircle,
};

/** Canais com implementação ativa hoje (espelha o hook). */
const IMPLEMENTED = new Set(['in_app', 'sound', 'browser', 'push_fcm']);

interface Props {
  modules: string[];
  channels: NotificationChannel[];
  preferences: ChannelPreference[];
  matrix: Map<string, boolean>;
  onToggle: (module: string, channelCode: string, value: boolean) => void;
}

export function NotificationMatrix({ modules, channels, matrix, onToggle }: Props) {
  // Para o canal `browser`, ao habilitar precisamos garantir permissão do navegador.
  const { requestBrowserPermission } = useNotificationDelivery();

  // Wrapper do onToggle que intercepta o canal `browser` para solicitar
  // permissão da Notification API antes de confirmar o toggle.
  const handleToggle = async (module: string, channelCode: string, value: boolean) => {
    if (channelCode === 'browser' && value) {
      const perm = await requestBrowserPermission();
      if (perm !== 'granted') {
        showToast.error('Permissão de notificações negada pelo navegador.');
        return; // não persiste o toggle
      }
    }
    onToggle(module, channelCode, value);
  };

  // Classes reutilizadas para a coluna fixa "Módulo".
  // Importante: usar fundo TOTALMENTE OPACO (bg-card) + sombra sutil à direita
  // para garantir que as colunas roláveis passem POR BAIXO sem transparecer.
  // Também elevamos z-index acima das demais células (que não definem z).
  const stickyBase =
    "sticky left-0 z-20 bg-card shadow-[1px_0_0_0_hsl(var(--border))]";


  return (
    <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th
              className={cn(
                stickyBase,
                "px-4 py-3 text-left font-semibold text-foreground/80",
              )}
            >
              Módulo
            </th>
            {channels.map(c => {
              const Icon = CHANNEL_ICON[c.code] ?? Bell;
              const impl = IMPLEMENTED.has(c.code);
              return (
                <th key={c.code} className="px-4 py-3 text-center min-w-[110px]">
                  <div className="flex flex-col items-center gap-1.5">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground/80">{c.name}</span>
                    {!impl && (
                      <Badge variant="outline" className="h-4 px-1.5 text-[9px] font-normal">
                        Em breve
                      </Badge>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {modules.map((m, idx) => {
            const meta = MODULE_META[m] ?? { label: m, icon: Bell };
            const Icon = meta.icon;
            const isZebra = idx % 2 === 1;
            return (
              <tr
                key={m}
                className={cn(
                  'border-t border-border/40',
                  isZebra && 'bg-muted/20',
                )}
              >
                {/*
                  A célula fixa NÃO usa bg-inherit (que falha em linhas sem fundo).
                  Em vez disso, aplica bg-card opaco e, nas linhas zebra, sobrepõe
                  o tom muted via classe condicional — mantendo o efeito visual
                  e garantindo opacidade total ao rolar horizontalmente.
                */}
                <td
                  className={cn(
                    stickyBase,
                    "px-4 py-3",
                    isZebra && "bg-muted",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-foreground/90">{meta.label}</span>
                  </div>
                </td>
                {channels.map(c => {
                  const key = `${m}::${c.code}`;
                  const checked = matrix.get(key) ?? false;
                  const impl = IMPLEMENTED.has(c.code);
                  return (
                    <td key={c.code} className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <IconToggle
                          checked={checked}
                          onCheckedChange={(v) => handleToggle(m, c.code, v)}
                          disabled={!impl}
                          aria-label={`${meta.label} via ${c.name}`}
                          tooltipOn="Desativar"
                          tooltipOff="Ativar"
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
